import React from 'react';
import { DialogActions, DialogContent } from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import { SavedSearchItem } from './SavedSearchItem';

import type { ModalProps } from 'src/hooks/useModalOpenState';
import useAlerts from 'src/hooks/useAlerts';

import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent, styledTag } from 'src/utils/styled';

import { useAdSearchState } from '../contexts/AdSearchStateContext';
import { useSavedSearchMutations } from '../hooks/useSavedSearchMutations';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import { useSavedSearches } from '../hooks/useSavedSearches';
import { useUserNotificationSubscriptions } from 'src/hooks/useUserNotificationSubscriptions';
import { NotificationSubscriptionStatus } from 'src/types/__generated__/graphql';
import Callout, { CalloutContent, CalloutTitle } from 'src/components/ui/Callout';
import { colours } from 'src/constants/colours';
import { useEnableAdSearchEmailNotification } from '../hooks/useEnableAdSearchEmailNotification';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0 1.5rem 1.5rem 1.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0.5rem 1.5rem;
    }
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};

const ScrollContainer = styledTag('div')`
    overflow-y: scroll;
    max-height: 25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

export const SavedSearchesModal: React.FC<ModalProps> = ({ open, onClose }) => {
    const { t } = useTranslation(['ad', 'notifications']);
    const userNotificationSubscriptions = useUserNotificationSubscriptions();
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();
    const { selectedSavedSearch, loadSavedSearch, resetAdSearchState } = useAdSearchState();
    const { updateSavedSearchLastViewedAt, deleteSavedSearch } = useSavedSearchMutations();
    const { enableAdSearchEmailNotification } = useEnableAdSearchEmailNotification();

    const { savedSearches, totalSavedSearches, loading, fetchMore } = useSavedSearches();
    const savedSearchTotalCount = totalSavedSearches ?? 0;

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        const actualDataLength = savedSearches?.length ?? 0;
        const allDataLoaded = actualDataLength >= savedSearchTotalCount;
        if (actualDataLength && bottom && !loading && !allDataLoaded) {
            try {
                await fetchMore({
                    variables: {
                        offset: actualDataLength,
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    const handleClickNewSearch = async () => {
        await resetAdSearchState();
        onClose();
    };

    const handleItemClick = async (searchId: string) => {
        logEventDynamic(AnalyticsEvent.SavedSearchSelected);
        await updateSavedSearchLastViewedAt({ id: searchId, lastViewedAt: new Date() });
        await loadSavedSearch(searchId);
        onClose();
    };

    const handleItemDeleteClick = async (searchId: string) => {
        try {
            await deleteSavedSearch(searchId);
            if (selectedSavedSearch?.id === searchId) {
                await resetAdSearchState();
            }
            addAlert({
                type: 'success',
                message: t('ad:searchDeleted'),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <DialogTitle onClose={onClose}>
                <Typography variant="h3">{t('ad:savedSearches')}</Typography>
            </DialogTitle>
            <DialogContent>
                {userNotificationSubscriptions?.savedSearch.emailNotificationsStatus ===
                    NotificationSubscriptionStatus.Inactive && (
                    <Callout fullWidth>
                        <CalloutTitle>{t('notifications:youAreNotCurrentlyNotified')}</CalloutTitle>
                        <CalloutContent>
                            <Typography variant="body2" color={colours.primary.main}>
                                {t('notifications:enableAdSearchEmailNotificationsHelper')}
                            </Typography>
                            <Button sx={{ marginTop: '0.8rem' }} onClick={enableAdSearchEmailNotification}>
                                {t('notifications:enableEmailNotifications')}
                            </Button>
                        </CalloutContent>
                    </Callout>
                )}
                {savedSearchTotalCount > 0 && (
                    <Box marginY="0.75rem">
                        <ScrollContainer onScroll={handleScroll}>
                            {savedSearches?.map((search) => (
                                <SavedSearchItem
                                    key={search.id}
                                    search={search}
                                    onItemClick={() => handleItemClick(search.id)}
                                    onDeleteClick={() => handleItemDeleteClick(search.id)}
                                />
                            ))}
                        </ScrollContainer>
                    </Box>
                )}
                {savedSearchTotalCount === 0 && !loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mb="1.5rem">
                        <Typography variant="body2">{t('ad:noSavedSearches')}</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {savedSearchTotalCount > 0 && <Button onClick={handleClickNewSearch}>{t('ad:newSearch')}</Button>}
            </DialogActions>
        </StyledDialog>
    );
};
